interface PerfEntry {
  label: string;
  timestamp: number;
  sinceStart: number;
  delta: number;
}

const perfLog: PerfEntry[] = [];
let startTime = 0;

/**
 * Record a performance milestone.
 * Call at strategic points to measure how long each phase takes.
 */
export function perfMark(label: string): void {
  const now = performance.now();

  if (perfLog.length === 0) {
    startTime = now;
  }

  const sinceStart = now - startTime;
  const delta =
    perfLog.length > 0 ? now - perfLog[perfLog.length - 1].timestamp : 0;

  perfLog.push({ label, timestamp: now, sinceStart, delta });
}

/**
 * Print a summary table to the console and return the data.
 */
export function perfSummary(): PerfEntry[] {
  const rows = perfLog.map((e) => ({
    Milestone: e.label,
    'Since start (ms)': Math.round(e.sinceStart),
    'Delta (ms)': Math.round(e.delta),
  }));

  console.table(rows);
  return perfLog;
}

/**
 * Send the perf log to the Vite dev server plugin, which writes it to disk.
 * Falls back to clipboard copy in production builds.
 */
export async function perfExport(tag = 'baseline'): Promise<void> {
  const payload = {
    tag,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    entries: perfLog.map((e) => ({
      label: e.label,
      sinceStartMs: Math.round(e.sinceStart),
      deltaMs: Math.round(e.delta),
    })),
  };

  try {
    const res = await fetch('/__perf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload, null, 2),
    });

    if (res.ok) {
      const { file } = await res.json();
      console.log(`perf data saved to: ${file}`);
    } else {
      console.warn('perf export failed, copying to clipboard instead');
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      console.log('perf data copied to clipboard');
    }
  } catch {
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    console.log('dev server not available — perf data copied to clipboard');
  }
}

/**
 * Reset all recorded marks.
 */
export function perfReset(): void {
  perfLog.length = 0;
  startTime = 0;
}

// Expose on window for console access
if (typeof window !== 'undefined') {
  Object.assign(window, { perfMark, perfSummary, perfExport, perfReset });
}
